import React from 'react'
import { Control, UseFormClearErrors, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InputControl, SelectControl } from '@/components/form-controls'
import { useDocumentTypes } from '@/hooks'
import { AuthorizationFormData } from '@/types/forms/'
import { getDocumentValidationRules } from '@/utils/documentValidationUtils'

interface GuardianSectionProps {
    control: Control<any>
    watch: UseFormWatch<any>
    clearErrors: UseFormClearErrors<any>
    isDisabled?: boolean
}

export const guardianFields = [
    'guardianName',
    'guardianDocumentType',
    'guardianDocumentNumber',
    'guardianMainEconomicActivity',
    'guardianSecondaryEconomicActivity'
] as const

export const GuardianSection: React.FC<GuardianSectionProps> = ({
    control,
    watch,
    clearErrors,
    isDisabled = false
}) => {
    const { t } = useTranslation()
    const { documentTypes, isLoading, hasError } = useDocumentTypes()

    // Field management is handled in exported guardianFields

    const [
        guardianName,
        guardianDocumentType,
        guardianDocumentNumber,
        guardianMainEconomicActivity,
        guardianSecondaryEconomicActivity
    ] = watch(guardianFields)

    const isGuardianSectionTouched = Boolean(
        guardianName ||
            guardianDocumentType ||
            guardianDocumentNumber ||
            guardianMainEconomicActivity ||
            guardianSecondaryEconomicActivity
    )

    // Clear errors if section is not touched
    React.useEffect(() => {
        if (!isGuardianSectionTouched) {
            clearErrors([...guardianFields])
        }
    }, [isGuardianSectionTouched, clearErrors])

    const validationRules = getDocumentValidationRules(guardianDocumentType)

    return (
        <section className="mt-6 rounded-md border border-gray-200 p-4">
            <h3 className="mb-4 text-sm font-semibold text-gray-800">
                {t('authorization.guardian.sectionTitle')}
            </h3>

            <div className="grid gap-4 md:grid-cols-2">
                <InputControl
                    name="guardianName"
                    control={control}
                    label={t('authorization.guardian.nameLabel')}
                    rules={{
                        validate: (value: string | undefined) =>
                            !isGuardianSectionTouched ||
                            Boolean(value) ||
                            t('forms.common.required.guardianNameRequired')
                    }}
                    isDisabled={isDisabled}
                    capitalize
                    maxWords={120}
                />

                {isLoading ? (
                    <div className="flex items-center justify-center p-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        <span className="ml-2 text-gray-600">{t('loading')}...</span>
                    </div>
                ) : hasError ? (
                    <div className="text-red-600 text-sm">
                        {t('Error al cargar tipos de documento')}
                    </div>
                ) : (
                    <SelectControl
                        name="guardianDocumentType"
                        control={control}
                        label={t('authorization.guardian.documentTypeLabel')}
                        options={documentTypes}
                        placeholder={t('Selecciona un tipo de documento')}
                        rules={{
                            validate: (value: string | undefined) =>
                                !isGuardianSectionTouched ||
                                Boolean(value) ||
                                t('forms.common.required.guardianDocumentTypeRequired')
                        }}
                        isDisabled={isDisabled}
                    />
                )}

                <InputControl
                    name="guardianDocumentNumber"
                    control={control}
                    label={t('authorization.guardian.documentNumberLabel')}
                    allowedPattern={validationRules.allowedPattern}
                    typeInput={validationRules.typeInput}
                    rules={{
                        validate: (value: string | undefined) =>
                            !isGuardianSectionTouched ||
                            Boolean(value) ||
                            t('forms.common.required.guardianDocumentNumberRequired'),
                        pattern: {
                            value: validationRules.pattern,
                            message: t('common.numberOnly')
                        },
                        minLength: {
                            value: validationRules.minLength,
                            message: t('basicDataInformation.required.documentNumberLength', {
                                maxLength: validationRules.maxLength
                            })
                        },
                        maxLength: {
                            value: validationRules.maxLength,
                            message: t('basicDataInformation.required.documentNumberLength', {
                                maxLength: validationRules.maxLength
                            })
                        }
                    }}
                    isDisabled={isDisabled}
                />

                <InputControl
                    name="guardianMainEconomicActivity"
                    control={control}
                    label={t('authorization.guardian.mainEconomicActivityLabel')}
                    rules={{
                        validate: (value: string | undefined) =>
                            !isGuardianSectionTouched ||
                            Boolean(value) ||
                            t('forms.common.required.guardianMainEconomicActivityRequired')
                    }}
                    isDisabled={isDisabled}
                    maxWords={200}
                />

                <div className="md:col-span-2">
                    <InputControl
                        name="guardianSecondaryEconomicActivity"
                        control={control}
                        label={t('authorization.guardian.secondaryEconomicActivityLabel')}
                        isDisabled={isDisabled}
                        maxWords={200}
                    />
                </div>
            </div>
        </section>
    )
}
